import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button, FormGroup } from 'react-bootstrap';
import {
  PaymentForm,
  CreditCard,
  ApplePay,
  GooglePay,
} from 'react-square-web-payments-sdk';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Spinner from './Spinner'
import StripeCheckoutForm from './StripeCheckoutForm'
import { getPrimaryColor } from '../Tools';
import { STRIPE_PUBLISHABLE_KEY, SQUARE_APPLICATION_ID, SQUARE_DEFAULT_LOCATION_ID } from '../environmentVariables';

const moment = require('moment')

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const ViolationDetails = (props) => {

  const [violation, setViolation] = useState(props.violation)
  const [detailsTab, setDetailsTab] = useState(props.detailsTab)
  const [disputeAcknowledged, setDisputeAcknowledged] = useState(false)
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [nameValid, setNameValid] = useState(false)
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailValid, setEmailValid] = useState(false)
  const [mobile, setMobile] = useState('')
  const [mobileTouched, setMobileTouched] = useState(false)
  const [mobileValid, setMobileValid] = useState(false)
  // Address fields
  const [address, setAddress] = useState('')
  const [addressTouched, setAddressTouched] = useState(false)
  const [addressValid, setAddressValid] = useState(false)
  const [city, setCity] = useState('')
  const [cityTouched, setCityTouched] = useState(false)
  const [cityValid, setCityValid] = useState(false)
  const [state, setState] = useState('')
  const [stateTouched, setStateTouched] = useState(false)
  const [stateValid, setStateValid] = useState(false)
  const [zipCode, setZipCode] = useState('')
  const [zipCodeTouched, setZipCodeTouched] = useState(false)
  const [zipCodeValid, setZipCodeValid] = useState(false)
  const [reason, setReason] = useState('')
  const [loadedImageCount, setLoadedImageCount] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [evidence1, setEvidence1] = useState(null)
  const [evidence2, setEvidence2] = useState(null)
  const [disputeSubmitted, setDisputeSubmitted] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [client] = useState(props.client)
  const [locationId, setLocationId] = useState(null)
  const [locationData, setLocationData] = useState(null)
  const [stripeClientSecret, setStripeClientSecret] = useState(null)
  // Resolved once we know how this citation will actually be charged:
  // 'square' (genuine Square location, or a Stripe location that fell back
  // because its connected account isn't verified yet — Stripe never charges
  // JustPark's platform account), 'stripe' (verified connected account),
  // 'retry' (transient failure checking readiness), or 'unavailable'
  // (Stripe location, not verified, no working Square fallback).
  const [effectiveProcessor, setEffectiveProcessor] = useState(null)
  // The PaymentIntent is only created after an explicit "Continue to
  // payment" click — not on every keystroke that happens to make name/email
  // validate — so a half-typed name never gets frozen into the intent's
  // metadata/receipt.
  const [contactConfirmed, setContactConfirmed] = useState(false)

  let applyEarlyBird = violation.early_bird_expiry && moment(violation.early_bird_expiry).isAfter(moment()) && (!violation.dispute || (violation.dispute?.status !== 'adjusted' && violation.dispute?.status !== 'accepted'))
  let applyPenalty = violation.penalty_start_at && moment(violation.penalty_start_at).isBefore(moment())

  const applicationId = SQUARE_APPLICATION_ID;

  useEffect(() => {
    props.getClientLocation(violation.client, violation.location, ['square_location_selected', 'square_locations', 'require_dispute_address', 'payment_processor']).then(locationData => {
      if (locationData.square_location_selected) {
        setLocationId(locationData.square_location_selected)
      } else {
        setLocationId(SQUARE_DEFAULT_LOCATION_ID)
      }
      setLocationData(locationData)
    })
  }, [])

  // Stripe's Payment Element needs a client_secret up front to mount, so we
  // create the PaymentIntent as soon as we know the contact details it will
  // be tagged with (used for the receipt via stripeWebhook), rather than
  // waiting for card submission like the Square flow does. The backend
  // enforces that Stripe never charges JustPark's platform account: it
  // either confirms the connected account is ready, hands back a Square
  // fallback, or (no fallback available) leaves us with neither.
  useEffect(() => {
    if (locationData?.payment_processor === 'square' && effectiveProcessor === null) {
      setEffectiveProcessor('square')
      return
    }
    if (locationData?.payment_processor === 'stripe' && contactConfirmed && nameValid && emailValid && effectiveProcessor === null) {
      props.createStripePaymentIntent(violation, parseFloat(getTotalToPay() + parseFloat(getServiceFee())).toFixed(2), name, email, parseFloat(getServiceFee())).then(result => {
        if (result?.ready && result?.clientSecret) {
          setStripeClientSecret(result.clientSecret)
          setEffectiveProcessor('stripe')
        } else if (result?.fallback === 'square') {
          setEffectiveProcessor('square')
        } else if (result?.errorMessage === 'processor_check_failed') {
          // Couldn't confirm readiness either way (network/Stripe API blip) —
          // this is not the same as "genuinely not verified", so offer a
          // retry instead of a dead-end "contact the operator" message.
          setEffectiveProcessor('retry')
        } else {
          setEffectiveProcessor('unavailable')
        }
      })
    }
  }, [locationData, nameValid, emailValid, effectiveProcessor, contactConfirmed])


  const height = window.innerHeight

  const getTotalToPay = () => {
    let totalToPay = violation.total
    if (applyEarlyBird) {
      totalToPay = violation.early_bird_total
    }
    if (applyPenalty) {
      totalToPay = violation?.dispute?.adjusted_discount ? parseFloat(violation?.dispute.original_amount) - parseFloat(violation?.dispute?.adjusted_discount) : parseFloat(violation.penalty_total)
    }
    return parseFloat(totalToPay)
  }

  const getServiceFee = () => {
    let service_fee = parseFloat(violation.service_fee)
    if (getTotalToPay()/100*5 > service_fee) {
      service_fee = parseFloat(getTotalToPay()/100*5)
    }
    return parseFloat(service_fee)
  }

  const paymentRequestOptions = {
    requestShippingAddress: false,
    requestBillingInfo: true,
    currencyCode: "USD",
    countryCode: "US",
    total: {
      label: "EnforcePlus",
      amount: parseFloat(getTotalToPay() + parseFloat(getServiceFee())).toFixed(2),
      pending: false
    },
    lineItems: [
      {
        label: "Subtotal",
        amount: parseFloat(getTotalToPay()).toFixed(2),
        pending: false
      },
      {
        label: "Service fee",
        amount: parseFloat(getServiceFee()).toFixed(2),
        pending: false
      }
    ]
  }

  useEffect(() => {
    if (name.length > 3) {
      setNameValid(true)
    } else {
      setNameValid(false)
    }
  }, [name])

  useEffect(() => {
    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
      setEmailValid(true)
    } else {
      setEmailValid(false)
    }
  }, [email])

  useEffect(() => {
    if (mobile.length === 10) {
      setMobileValid(true)
    } else {
      setMobileValid(false)
    }
  }, [mobile])

  // Address validation effects
  useEffect(() => {
    if (address.length > 5) {
      setAddressValid(true)
    } else {
      setAddressValid(false)
    }
  }, [address])

  useEffect(() => {
    if (city.length > 2) {
      setCityValid(true)
    } else {
      setCityValid(false)
    }
  }, [city])

  useEffect(() => {
    if (state.length === 2) {
      setStateValid(true)
    } else {
      setStateValid(false)
    }
  }, [state])

  useEffect(() => {
    if (/^\d{5}(-\d{4})?$/.test(zipCode)) {
      setZipCodeValid(true)
    } else {
      setZipCodeValid(false)
    }
  }, [zipCode])

  useEffect(() => {
    setDisputeAcknowledged(false)
    setDisputeSubmitted(false)
  }, [detailsTab])

  useEffect(() => {
    setDisputeSubmitted(props.submittedDispute)
  }, [props.submittedDispute])

  const getCitationTerm = () => {
    if (violation?.citation_term && violation.citation_term.length > 0) {
      return violation.citation_term
    }
    return 'Citation'
  }

  const getTitle = () => {
    switch (detailsTab) {
      case "photos":
        return 'View photos'
      case "dispute":
        return 'Dispute ' + getCitationTerm().toLowerCase()
      case "pay":
        return 'Pay ' + getCitationTerm().toLowerCase()
    }
  }

  const imageLoaded = () => {
    if (loadedImageCount + 1 === violation.images?.length) {
      setImagesLoaded(true)
    } else {
      setLoadedImageCount(loadedImageCount + 1)
    }
  }

  return (
    <>
      <div style={{height: 50, width: '100%', borderTopLeftRadius: 50, borderTopRightRadius: 50, paddingTop: 16, textAlign: 'center'}}>
        <span style={{fontSize: 24, fontWeight: 'bold'}}>{getTitle()}</span>
      </div>
      <Row style={{marginTop: 16, marginBottom: 8}}>
        <Col style={{paddingLeft: 2, paddingRight: 2}} className='text-center'>
          <Button
            className='btn btn-outline-secondary'
            style={{backgroundColor: 'white', fontSize: 14, width: 125, paddingLeft: 0, paddingRight: 0}}
            onClick={() => detailsTab === 'photos' ? props.onClose() : setDetailsTab('photos')}>
              {detailsTab === 'photos' ? 'Close' : 'View photos'}
          </Button>
        </Col>
        <Col style={{paddingLeft: 2, paddingRight: 2}} className='text-center'>
          <Button
            className='btn btn-outline-secondary'
            style={{backgroundColor: 'white', fontSize: 14, width: 125, paddingLeft: 0, paddingRight: 0}}
            onClick={() => detailsTab === 'dispute' ? props.onClose() : setDetailsTab('dispute') }>
              {detailsTab === 'dispute' ? 'Close' : 'Dispute'}
          </Button>
        </Col>
      </Row>
      <Row style={{marginTop: 12, marginBottom: 16}}>
        <Col style={{paddingLeft: 26, paddingRight: 26}} className='text-center'>
          <Button
            className='btn btn-primary'
            style={{color: 'white', fontSize: 14, width: '100%', paddingLeft: 0, paddingRight: 0, backgroundColor: getPrimaryColor(client), borderColor: getPrimaryColor(client)}}
            onClick={() => detailsTab === 'pay' ? props.onClose() : setDetailsTab('pay')}>
              {detailsTab === 'pay' ? 'Close' : 'Pay now'}
          </Button>
        </Col>
      </Row>
      {detailsTab === 'photos' && (
        <div style={{paddingBottom: 50, height: height - 150, overflowX: 'hidden'}}>
          {(!violation.images || violation.images?.length === 0) && (
            <Row style={{marginBottom: 16}} key={'image-row-0'}>
              <Col className='text-center'>
                No images found
              </Col>
            </Row>
          )}
          {violation.images?.map((image, index) => (
            <Row style={{marginBottom: 16}} key={'image-row-' + index}>
              <Col style={{width: 320, display: imagesLoaded ? 'block' : 'none'}}>
                <div style={{height: 220, width: 320, overflow: 'hidden', marginLeft: 'auto', marginRight: 'auto'}}>
                  <img src={image.url} style={{width: '100%', height: '100%', objectFit: 'cover'}} onLoad={() => imageLoaded()} />
                </div>
                <div style={{width: 320, marginLeft: 'auto', marginRight: 'auto'}}>
                  <span>{moment(image.created_at).format('M/D/Y h:mm a')}</span>
                </div>
              </Col>
              <Col style={{width: 320, display: imagesLoaded ? 'none' : 'block'}}>
                <div style={{
                  width: '100%',
                  height: 240,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Spinner color={getPrimaryColor(client)} size="30px" />
                </div>
              </Col>
            </Row>
          ))}
        </div>
      )}
      {detailsTab === 'dispute' && violation.dispute && (
        <div style={{height: height - 150, overflowX: 'hidden', paddingBottom: 50}}>
          <Row>
            <Col style={{paddingTop: 16}}>
              {violation.dispute.status === 'open' && (
                <span>Your dispute has been received and is being reviewed.</span>
              )}
              {violation.dispute.status === 'adjusted' && (
                <span>Your dispute has been reviewed and your balance has been adjusted accordingly.</span>
              )}
              {violation.dispute.status === 'rejected' && (
                <span>Your dispute has been received and reviewed and unfortunately it has been rejected. Please pay the outstanding balance as soon as possible to avoid further penalty.</span>
              )}
              {violation.dispute.status === 'accepted' && (
                <span>Your dispute has been received and reviewed and was accepted. There is nothing to pay.</span>
              )}
            </Col>
          </Row>
        </div>
      )}
      {detailsTab === 'dispute' && !violation.dispute && (
        !disputeSubmitted ? (
          <div style={{height: height - 150, overflowX: 'hidden', paddingBottom: 50}}>
            {!disputeAcknowledged && (
              <Row>
                <Col style={{paddingTop: 16}}>
                  <div className="alert alert-danger" role="alert" style={{paddingTop: 8, paddingBottom: 8}}>
                    <span style={{fontSize: 14, lineHeight: 1, color: 'red'}}>First read this:<br />
                    <br />
                    Common reasons that DO NOT support dismissing a {getCitationTerm().toLowerCase()} are:<br /><br/>
                    I was only in violation for a minute;<br/>
                    I did not know that I could not park there;<br />
                    I think the penalty is too high;<br />
                    I did not see the sign or curb markings;<br />
                    I cannot afford to pay the fine;<br />
                    I have never had a ticket before;<br />
                    I will never do it again;<br />
                    I couldn't pay<br /></span>
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <div className="form-check form-check-inline">
                  <label className="form-check-label" for="inlineCheckbox1">I acknowledge and understand</label>
                  <input className="form-check-input" type="checkbox" id="inlineCheckbox1" onClick={(event) => setDisputeAcknowledged(event.target.checked)} style={{backgroundColor: 'grey'}} />   
                </div>
              </Col>
            </Row>
            {!disputeAcknowledged && (
              <Row style={{marginTop: 16, marginBottom: 80}}>
                <Col>
                  <span onClick={() => setDetailsTab('pay')}>Or tap here to pay {getCitationTerm().toLowerCase()}</span> 
                </Col>
              </Row>
            )}
            {disputeAcknowledged && (
              <Form style={{marginTop: 16, marginBottom: 80}}>
                <FormGroup style={{marginBottom: 16}}>
                  <label for='name' style={{color: '#C5C7CA'}}>Name *</label>
                  <input type='text' className={nameTouched ? nameValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='name' onBlur={() => setNameTouched(true)} placeholder='Enter your name' value={name} onChange={(event) => setName(event.target.value)}/>
                  {!nameValid && nameTouched && (
                    <div className="invalid-feedback">
                      Please enter your name.
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{marginBottom: 16}}>
                  <label for='email' style={{color: '#C5C7CA'}}>Email address *</label>
                  <input type='email' className={emailTouched ? emailValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='email' onBlur={() => setEmailTouched(true)} placeholder='Enter your email' value={email} onChange={(event) => setEmail(event.target.value)}/>
                  {!emailValid && emailTouched && (
                    <div className="invalid-feedback">
                      Please enter a valid email address.
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{marginBottom: 16}}>
                  <label for='mobile' style={{color: '#C5C7CA'}}>Mobile *</label>
                  <input type='mobile' className={mobileTouched ? mobileValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='mobile' onBlur={() => setMobileTouched(true)} placeholder='Enter your mobile' value={mobile} onChange={(event) => setMobile(event.target.value?.replace(/\D/gim, ''))}/>
                  {!mobileValid && mobileTouched && (
                    <div className="invalid-feedback">
                      Please enter a valid mobile.
                    </div>
                  )}
                </FormGroup>
                {locationData?.require_dispute_address && (
                  <>
                    <FormGroup style={{marginBottom: 16}}>
                      <label for='dispute-address' style={{color: '#C5C7CA'}}>Street Address *</label>
                      <input type='text' className={addressTouched ? addressValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='dispute-address' autocomplete='off' onBlur={() => setAddressTouched(true)} placeholder='Enter your street address' value={address} onChange={(event) => setAddress(event.target.value)}/>
                      {!addressValid && addressTouched && (
                        <div className="invalid-feedback">
                          Please enter a valid street address.
                        </div>
                      )}
                    </FormGroup>
                    <FormGroup style={{marginBottom: 16}}>
                      <label for='dispute-city' style={{color: '#C5C7CA'}}>City *</label>
                      <input type='text' className={cityTouched ? cityValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='dispute-city' autocomplete='off' onBlur={() => setCityTouched(true)} placeholder='Enter your city' value={city} onChange={(event) => setCity(event.target.value)}/>
                      {!cityValid && cityTouched && (
                        <div className="invalid-feedback">
                          Please enter a valid city.
                        </div>
                      )}
                    </FormGroup>
                    <Row style={{marginBottom: 16}}>
                      <Col xs={6}>
                        <FormGroup>
                          <label for='dispute-state' style={{color: '#C5C7CA'}}>State *</label>
                          <input type='text' className={stateTouched ? stateValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='dispute-state' autocomplete='off' onBlur={() => setStateTouched(true)} placeholder='CA' value={state} onChange={(event) => setState(event.target.value.toUpperCase().slice(0, 2))}/>
                          {!stateValid && stateTouched && (
                            <div className="invalid-feedback">
                              Please enter a valid 2-letter state code.
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col xs={6}>
                        <FormGroup>
                          <label for='dispute-zipcode' style={{color: '#C5C7CA'}}>ZIP Code *</label>
                          <input type='text' className={zipCodeTouched ? zipCodeValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='dispute-zipcode' autocomplete='off' onBlur={() => setZipCodeTouched(true)} placeholder='12345' value={zipCode} onChange={(event) => setZipCode(event.target.value.replace(/\D/g, '').slice(0, 5))}/>
                          {!zipCodeValid && zipCodeTouched && (
                            <div className="invalid-feedback">
                              Please enter a valid ZIP code.
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
                <FormGroup style={{marginBottom: 16}}>
                  <label for='reason' style={{color: '#C5C7CA'}}>Reason your {getCitationTerm().toLowerCase()} should be dismissed</label>
                  <textarea style={{height: 100}} className={'form-control'} id='name' placeholder='Enter your reasoning' value={reason} onChange={(event) => setReason(event.target.value)}/>
                </FormGroup>
                <Form.Group controlId="evidence1" style={{marginBottom: 16}}>
                  <Form.Label style={{color: '#C5C7CA'}}>Upload supporting evidence (optional)</Form.Label>
                  <Form.Control type="file" onChange={(event) => setEvidence1(event.target.files[0])}/>
                </Form.Group>
                <Form.Group controlId="evidence2" style={{marginBottom: 16}}>
                  <Form.Label style={{color: '#C5C7CA'}}>Upload supporting evidence (optional)</Form.Label>
                  <Form.Control type="file" onChange={(event) => setEvidence2(event.target.files[0])}/>
                </Form.Group>
                <FormGroup style={{marginBottom: 20, textAlign: 'center'}}>
                  {props.submittingDispute ? (
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Spinner color={getPrimaryColor(client)} size="30px" />
                    </div>
                  ) : (
                    <Button
                      className='btn btn-lg'
                      style={{color: 'white', fontSize: 18, backgroundColor: getPrimaryColor(client), borderColor: getPrimaryColor(client)}}
                      onClick={() => {props.submitDispute(violation, name, email, reason, evidence1, evidence2, mobile, locationData?.require_dispute_address ? { address, city, state, zipCode } : null)}}
                      disabled={!nameValid || !emailValid || !mobileValid || (locationData?.require_dispute_address && (!addressValid || !cityValid || !stateValid || !zipCodeValid))}>SUBMIT</Button>
                  )}
                </FormGroup>
                <div style={{marginBottom: 60, textAlign: 'center'}}>
                  {props.submitDisputeFailed && (
                    <span style={{color: 'red'}}>Sorry, your dispute could not be sent. Please try again</span>
                  )} 
                </div>
              </Form>
            )}
          </div>
        ) : (
          <div style={{height: height - 150, overflowX: 'hidden', paddingBottom: 50, textAlign: 'center', paddingTop: 24}}>
            <span>Thank you, we have received your information and will review it.</span>
          </div>
        )
      )}
      {detailsTab === 'pay' && (
        <Row style={{marginTop: 12}}>
          <Col style={{paddingLeft: 16, paddingRight: 16}}>
            {violation.violations.map(item => (
              <Row style={{marginTop: 4}}>
                <Col xs={8}><span>{item.name}</span></Col>
                <Col xs={4} style={{textAlign: 'right'}}><span>${parseFloat(item.amount).toFixed(2)}</span></Col>
              </Row>
            ))}
            {applyEarlyBird && (
              <Row style={{marginTop: 4}}>
                <Col xs={8}><span>Prompt payment discount</span></Col>
                <Col xs={4} style={{textAlign: 'right'}}><span>-${parseFloat(parseFloat(violation.violations_total) - parseFloat(violation.early_bird_total)).toFixed(2)}</span></Col>
              </Row>
            )}
            {applyPenalty && !violation?.dispute?.adjusted_discount && (
              <Row style={{marginTop: 4}}>
                <Col xs={8}><span>Late payment penalty</span></Col>
                <Col xs={4} style={{textAlign: 'right'}}><span>${parseFloat(parseFloat(violation.penalty_total) - parseFloat(violation.violations_total)).toFixed(2)}</span></Col>
              </Row>
            )}
            {!!violation.dispute?.adjusted_discount && (
              <Row style={{marginTop: 4}}>
                <Col xs={8}><span>Adjusted discount</span></Col>
                <Col xs={4} style={{textAlign: 'right'}}><span>-${parseFloat(violation.dispute.adjusted_discount).toFixed(2)}</span></Col>
              </Row>
            )}
            {getServiceFee() > 0 && (
              <Row style={{marginTop: 4}}>
                <Col xs={8}><span>Service fee</span></Col>
                <Col xs={4} style={{textAlign: 'right'}}><span>${parseFloat(getServiceFee()).toFixed(2)}</span></Col>
              </Row>
            )}
            <Row style={{marginTop: 8}}>
              <Col xs={8}><span style={{fontWeight: 'bold'}}>Total due</span></Col>
              <Col xs={4} style={{textAlign: 'right'}}><span style={{fontWeight: 'bold'}}>${parseFloat(getTotalToPay() + parseFloat(getServiceFee())).toFixed(2)}</span></Col>
            </Row>
            <Row style={{marginTop: 8}}>
              {!!processing || locationId === null ? (
                <div style={{
                  width: '100%',
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Spinner color={getPrimaryColor(client)} size="30px" />
                  <div><span>Processing payment, please wait...</span></div>
                </div>
              ) : locationData?.payment_processor === 'stripe' && effectiveProcessor !== 'square' ? (
                <>
                  <Form style={{marginTop: 12, marginBottom: 12}}>
                    <FormGroup style={{marginBottom: 8}}>
                      {/* Locked once the citizen confirms — the PaymentIntent gets tagged with these values and Stripe's metadata can't be updated client-side afterward. */}
                      <input type='text' disabled={contactConfirmed} className={nameTouched ? nameValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='name' onBlur={() => setNameTouched(true)} placeholder='Enter your full name *' value={name} onChange={(event) => setName(event.target.value)}/>
                      {!nameValid && nameTouched && (
                        <div className="invalid-feedback">
                          Please enter your name.
                        </div>
                      )}
                    </FormGroup>
                    <FormGroup style={{marginBottom: 8}}>
                      <input type='email' disabled={contactConfirmed} className={emailTouched ? emailValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='email' onBlur={() => setEmailTouched(true)} placeholder='Enter your email address *' value={email} onChange={(event) => setEmail(event.target.value)}/>
                      {!emailValid && emailTouched && (
                        <div className="invalid-feedback">
                          Please enter a valid email address.
                        </div>
                      )}
                    </FormGroup>
                  </Form>
                  {!(nameValid && emailValid) ? (
                    <div style={{textAlign: 'center', color: '#C5C7CA'}}>
                      <span>Enter your name and email above to continue to payment.</span>
                    </div>
                  ) : !contactConfirmed ? (
                    <div style={{textAlign: 'center'}}>
                      <Button
                        className='btn btn-lg'
                        style={{color: 'white', fontSize: 18, backgroundColor: getPrimaryColor(client), borderColor: getPrimaryColor(client)}}
                        onClick={() => setContactConfirmed(true)}>
                          Continue to payment
                      </Button>
                    </div>
                  ) : effectiveProcessor === 'unavailable' ? (
                    <div style={{textAlign: 'center'}}>
                      <span style={{color: 'red'}}>Online payment isn't available for this location right now. Please contact the operator directly to pay or dispute this {getCitationTerm().toLowerCase()}.</span>
                    </div>
                  ) : effectiveProcessor === 'retry' ? (
                    <div style={{textAlign: 'center'}}>
                      <span style={{color: 'red'}}>Sorry, we couldn't start your payment. </span>
                      <span style={{textDecoration: 'underline', cursor: 'pointer'}} onClick={() => setEffectiveProcessor(null)}>Try again</span>
                    </div>
                  ) : effectiveProcessor === 'stripe' && stripeClientSecret ? (
                    <Elements stripe={stripePromise} options={{clientSecret: stripeClientSecret}}>
                      <StripeCheckoutForm violation={violation} primaryColor={getPrimaryColor(client)} />
                    </Elements>
                  ) : (
                    <div style={{width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <Spinner color={getPrimaryColor(client)} size="30px" />
                    </div>
                  )}
                </>
              ) : (
                <PaymentForm
                  applicationId={applicationId}
                  locationId={locationId}
                  createPaymentRequest={() => paymentRequestOptions}
                  methodsSupported={{applePay: true, googlePay: true, card: true}}
                  cardTokenizeResponseReceived={(tokenResult) => {setProcessing(true); props.onPayment(violation, tokenResult, parseFloat(getTotalToPay() + parseFloat(getServiceFee())), name, email, parseFloat(getServiceFee()))}}
                >
                  <ApplePay />
                  <div style={{height: 16}}></div>
                  <GooglePay />
                  <Form style={{marginTop: 12, marginBottom: 12}}>
                    <FormGroup style={{marginBottom: 8}}>
                      <input type='text' className={nameTouched ? nameValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='name' onBlur={() => setNameTouched(true)} placeholder='Enter your full name *' value={name} onChange={(event) => setName(event.target.value)}/>
                      {!nameValid && nameTouched && (
                        <div className="invalid-feedback">
                          Please enter your name.
                        </div>
                      )}
                    </FormGroup>
                    <FormGroup style={{marginBottom: 8}}>
                      <input type='email' className={emailTouched ? emailValid ? 'form-control is-valid' : 'form-control is-invalid' : 'form-control'} id='email' onBlur={() => setEmailTouched(true)} placeholder='Enter your email address *' value={email} onChange={(event) => setEmail(event.target.value)}/>
                      {!emailValid && emailTouched && (
                        <div className="invalid-feedback">
                          Please enter a valid email address.
                        </div>
                      )}
                    </FormGroup>
                  </Form>
                  <CreditCard
                    includeInputLabels={true}
                    buttonProps={{
                      isLoading: !(emailValid && nameValid),
                    }}
                  />
                </PaymentForm>
              )}
              
            </Row>
          </Col>
        </Row>
      )}
    </>
  )
  
}

export default ViolationDetails;
